import { SwipeVertical } from '@mui/icons-material';
import {
  GridLegacy as Grid,
  styled,
  type Theme,
  useMediaQuery,
} from '@mui/material';
import type { CaseStudy } from '@rumblefish/strapi/strapiTypes';
import { colors } from '@rumblefish/ui';
import clsx from 'clsx';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatFilterNameToValue } from '../CaseStudies.utils';
import type { CaseStudiesGridProps } from './CaseStudiesGrid.types';
import CaseStudyBlock from './CaseStudyBlock';

const PREFIX = 'CaseStudiesGrid';

const classes = {
  paginationContainer: `${PREFIX}-paginationContainer`,
  paginationLink: `${PREFIX}-paginationLink`,
  paginationLinkActive: `${PREFIX}-paginationLinkActive`,
  swipeIcon: `${PREFIX}-swipeIcon`,
};

const CaseStudiesWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(6),
  },
  [`& .${classes.paginationContainer}`]: {
    paddingTop: '20px',
    paddingBottom: '50px',
  },

  [`& .${classes.paginationLink}`]: {
    cursor: 'pointer',
    fontWeight: 'bold',
    borderRadius: '100%',
    color: colors.white,
    background: 'transparent',
    width: '40px',
    textDecoration: 'none',
    display: 'inline-block',
    lineHeight: '40px',
    textAlign: 'center',
    margin: '0px 10px 0px 10px',
    border: 'none',
    '&:hover': {
      background: theme.palette.raspberryColors?.PLUS_1,
    },
  },

  [`& .${classes.paginationLinkActive}`]: {
    cursor: 'initial',
    background: theme.palette.raspberryColors?.PLUS_1,
    color: colors.white,
    marginRight: '5px',
  },

  '@keyframes swipe-icon-animation': {
    '20%': {
      transform: 'translateY(0px)',
    },
    '30%': {
      transform: 'rotate(30deg)',
    },
    '40%': {
      transform: 'rotate(0deg)',
    },
    '50%': {
      transform: 'rotate(-30deg)',
    },
    '60%': {
      transform: 'rotate(0deg)',
    },
  },

  [`& .${classes.swipeIcon}`]: {
    display: 'none',
    position: 'fixed',
    opacity: 1,
    left: '30px',
    bottom: '20px',
    height: '40px',
    width: '40px',
    translateX: '-50%',
    transition: 'opacity 0.2s ease-in-out',
    color: theme.palette.raspberryColors?.PLUS_1,
    animation: `swipe-icon-animation 3s infinite`,
    animationDelay: '1s',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
}));

export const CaseStudiesGrid: React.FC<CaseStudiesGridProps> = ({
  filter,
  pagination,
  data,
  shallowRedirect,
  filtersContainerHeight,
  showSwipeIcon,
}) => {
  const filteredData: CaseStudy[] = useMemo(() => {
    if (filter.length > 0) {
      return data.filter((item) => {
        return (
          item.case_study_services.some(
            (service) =>
              service?.name && formatFilterNameToValue(service?.name) === filter
          ) ||
          item.case_study_technologies.some(
            (technology) =>
              technology?.name &&
              formatFilterNameToValue(technology?.name) === filter
          )
        );
      });
    }

    return data;
  }, [filter, data]);

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const casesPerPage = 6;
  const numberOfPages = Math.ceil(filteredData.length / casesPerPage);
  const pageNumbers = Array.from({ length: numberOfPages }, (_, i) => i + 1);

  const indexOfLastcase = pagination * casesPerPage;
  const indexOfFirstcase = indexOfLastcase - casesPerPage;
  useEffect(() => {
    if (pageNumbers.length > 0 && pagination > pageNumbers.length) {
      const newPage = pageNumbers.slice(-1)[0];
      shallowRedirect({ filter, page: newPage });
    }
  }, [pageNumbers, pagination]);
  const [currentCaseStudyIndex, setCurrentCaseStudyIndex] = useState<number>(
    (pagination - 1) * casesPerPage
  );
  const [currentCaseStudy, setCurrentCaseStudy] = useState(filteredData[0]);

  const handleRedirect = (page: number) => {
    shallowRedirect({ filter, page });
  };

  const onSwipeUp = useCallback(() => {
    const nextCaseStudyIndex =
      currentCaseStudyIndex < filteredData.length - 1
        ? currentCaseStudyIndex + 1
        : 0;
    if (nextCaseStudyIndex != currentCaseStudyIndex) {
      setCurrentCaseStudyIndex(nextCaseStudyIndex);
    }
  }, [currentCaseStudyIndex, filter]);

  const onSwipeDown = useCallback(() => {
    const previousCaseStudyIndex =
      currentCaseStudyIndex > 0
        ? currentCaseStudyIndex - 1
        : filteredData.length - 1;
    if (previousCaseStudyIndex != currentCaseStudyIndex) {
      setCurrentCaseStudyIndex(previousCaseStudyIndex);
    }
  }, [currentCaseStudyIndex, filter]);

  const isSwipeIconVisible = useCallback(
    () => showSwipeIcon && filteredData.length > 1,
    [showSwipeIcon, filteredData.length]
  );
  useEffect(() => {
    filteredData[0] && setCurrentCaseStudy(filteredData[0]);
    setCurrentCaseStudyIndex(0);
  }, [filteredData]);

  useEffect(() => {
    if (
      currentCaseStudy != filteredData[currentCaseStudyIndex] &&
      currentCaseStudyIndex >= 0 &&
      currentCaseStudyIndex < filteredData.length
    )
      setCurrentCaseStudy(filteredData[currentCaseStudyIndex]);
  }, [currentCaseStudyIndex]);
  return (
    <CaseStudiesWrapper>
      {mdDown ? (
        <>
          <AnimatePresence mode="wait">
            {currentCaseStudy && currentCaseStudy.documentId && (
              <CaseStudyBlock
                key={currentCaseStudy.documentId}
                caseStudy={currentCaseStudy}
                offsetTop={filtersContainerHeight}
                onSwipeDown={onSwipeDown}
                onSwipeUp={onSwipeUp}
              />
            )}
          </AnimatePresence>

          <SwipeVertical
            className={classes.swipeIcon}
            sx={
              isSwipeIconVisible()
                ? {
                    opacity: '1 !important',
                    zIndex: 60,
                  }
                : {
                    opacity: '0 !important',
                    zIndex: -1,
                  }
            }
          />
        </>
      ) : (
        filteredData
          .slice(indexOfFirstcase, indexOfLastcase)
          .map((caseStudy, index) => (
            <CaseStudyBlock
              key={(caseStudy.documentId ?? '') + (caseStudy?.client ?? '')}
              caseStudy={caseStudy}
              direction={index % 2 === 0 ? 'row' : 'row-reverse'}
              offsetTop={filtersContainerHeight}
              onSwipeDown={() => null}
              onSwipeUp={() => null}
            />
          ))
      )}

      {!mdDown && (
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          direction="row"
          className={classes.paginationContainer}>
          {numberOfPages > 1 &&
            pageNumbers.map((number, index) => (
              <Grid item key={index}>
                <button
                  onClick={() => handleRedirect(number)}
                  disabled={Number(number) === Number(pagination)}
                  className={clsx(classes.paginationLink, {
                    [classes.paginationLinkActive]:
                      Number(number) === Number(pagination),
                  })}>
                  {number}
                </button>
              </Grid>
            ))}
        </Grid>
      )}
    </CaseStudiesWrapper>
  );
};
